import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MCQQuestion } from "@/lib/powtoon-be/types";
import { emptyQuestion } from "@/lib/scenarios/mcq";

const MAX_ANSWERS = 8;

export function QuestionListEditor({
  questions,
  onChange,
  disabled,
  hasError,
}: {
  questions: MCQQuestion[];
  onChange: (next: MCQQuestion[]) => void;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const list = questions.length === 0 ? [emptyQuestion()] : questions;
  const isPlaceholder = questions.length === 0;

  function updateQuestion(idx: number, patch: Partial<MCQQuestion>) {
    if (isPlaceholder) {
      onChange([{ ...list[0], ...patch }]);
      return;
    }
    onChange(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }

  function setAnswerText(qIdx: number, aIdx: number, text: string) {
    const q = list[qIdx];
    const answers = q.answers.map((a, i) => (i === aIdx ? { text } : a));
    updateQuestion(qIdx, { answers });
  }

  function setMinCorrect(qIdx: number, n: number) {
    updateQuestion(qIdx, { amount_of_correct_answers_to_pass: n });
  }

  function addAnswer(qIdx: number) {
    const q = list[qIdx];
    if (q.answers.length >= MAX_ANSWERS) return;
    updateQuestion(qIdx, { answers: [...q.answers, { text: "" }] });
  }

  function removeAnswer(qIdx: number, aIdx: number) {
    const q = list[qIdx];
    if (q.answers.length <= 1) return;
    const answers = q.answers.filter((_, i) => i !== aIdx);
    const min = Math.min(q.amount_of_correct_answers_to_pass, answers.length);
    updateQuestion(qIdx, {
      answers,
      amount_of_correct_answers_to_pass: Math.max(1, min),
    });
  }

  function removeQuestion(idx: number) {
    onChange(questions.filter((_, i) => i !== idx));
  }

  function addQuestion() {
    onChange([...(isPlaceholder ? [] : questions), emptyQuestion()]);
  }

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border bg-card p-3",
        hasError && "border-destructive",
      )}
    >
      {list.map((q, qIdx) => (
        <div
          key={q.id}
          className={cn(
            "rounded-md border bg-background p-3 space-y-3",
            isPlaceholder && "opacity-70",
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Question {qIdx + 1}
              </Label>
              <Textarea
                value={q.question}
                onChange={(e) => updateQuestion(qIdx, { question: e.target.value })}
                placeholder="Type the question here…"
                disabled={disabled}
                className="min-h-[60px]"
              />
            </div>
            {!isPlaceholder && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground hover:text-destructive"
                onClick={() => removeQuestion(qIdx)}
                disabled={disabled}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Valid answers (each item is a correct answer)
            </Label>
            {q.answers.map((a, aIdx) => (
              <div key={aIdx} className="flex items-center gap-2">
                <span className="w-5 text-xs text-muted-foreground text-right">
                  {aIdx + 1}.
                </span>
                <Input
                  value={a.text}
                  onChange={(e) => setAnswerText(qIdx, aIdx, e.target.value)}
                  placeholder={`Answer item ${aIdx + 1}`}
                  disabled={disabled}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-muted-foreground hover:text-destructive"
                  onClick={() => removeAnswer(qIdx, aIdx)}
                  disabled={disabled || q.answers.length <= 1}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {q.answers.length < MAX_ANSWERS && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => addAnswer(qIdx)}
                disabled={disabled}
                className="h-7 px-2 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Add answer item
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Label className="text-xs text-muted-foreground">
              Minimum correct items to pass:
            </Label>
            <Select
              value={String(q.amount_of_correct_answers_to_pass)}
              onValueChange={(v) => setMinCorrect(qIdx, Number(v))}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: Math.max(1, q.answers.length) }, (_, i) => i + 1).map(
                  (n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              of {q.answers.length} listed
            </span>
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addQuestion}
          disabled={disabled}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add question
        </Button>
      </div>
    </div>
  );
}